import graphql from 'babel-plugin-relay/macro';
import React from 'react';
import {usePaginationFragment, useMutation} from 'react-relay/hooks';
import ConnectionHandler from 'relay-connection-handler-plus';
import useFilters from 'src/hooks/useFilters';
import {showEventNotification} from 'src/utils/notifications';

import Table from '../../../components/table';
import DateCell from '../../../components/table/cell-types/DateCell';
import SecondaryTextCell from '../../../components/table/cell-types/SecondaryTextCell';
import {listPageValveFamiliesQuery} from './__generated__/listPageValveFamiliesQuery.graphql';
import {AssetFamiliesOrder} from './__generated__/ValveFamiliesPaginationQuery.graphql';
import {ValveFamiliesTable_valveFamilies$key} from './__generated__/ValveFamiliesTable_valveFamilies.graphql';
import {ValveFamiliesTableDeleteValveFamilyMutation} from './__generated__/ValveFamiliesTableDeleteValveFamilyMutation.graphql';
import TableFilters from './TableFilters';

type Props = {
  valveFamilies: ValveFamiliesTable_valveFamilies$key;
};

type Filters = {
  name?: string;
  orderBy?: AssetFamiliesOrder;
};

function ValveFamiliesTable(props: Props) {
  const {data, loadNext, hasNext, refetch} = usePaginationFragment<
    listPageValveFamiliesQuery,
    ValveFamiliesTable_valveFamilies$key
  >(
    graphql`
      fragment ValveFamiliesTable_valveFamilies on Query
        @argumentDefinitions(
          first: {type: "Int!"}
          after: {type: "String"}
          name: {type: "String"}
          orderBy: {type: "AssetFamiliesOrder"}
        )
        @refetchable(queryName: "ValveFamiliesPaginationQuery") {
        valveFamilies(first: $first, after: $after, orderBy: $orderBy, name: $name)
          @connection(key: "ValveFamiliesList_valveFamilies") {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              createdAt
              name
              manufacturer {
                id
                name
              }
            }
          }
        }
      }
    `,
    props.valveFamilies,
  );

  const [commit] = useMutation<ValveFamiliesTableDeleteValveFamilyMutation>(
    deleteValveFamilyMutation,
  );

  const {addFilters, setFilters, isTransitioning} = useFilters<Filters>({}, refetch);

  return (
    <Table
      filters={() => (
        <TableFilters onApplyFilters={handleOnApplyFilter} onResetFilters={handleOnResetFilter} />
      )}
      actions={{show: false, delete: true, edit: true, create: true}}
      totalDataLength={data.valveFamilies.totalCount}
      columns={columns}
      isTransitioning={isTransitioning}
      onPaginate={() => hasNext && loadNext(50)}
      onSort={handleOnSort}
      onDelete={handleDelete}
      data={data.valveFamilies.edges}
    />
  );

  function handleOnApplyFilter(filters: {text: string}) {
    addFilters({name: filters.text});
  }

  function handleOnResetFilter() {
    setFilters({});
  }

  async function handleOnSort(sortByConfig: any[]) {
    if (!sortByConfig.length) {
      addFilters({orderBy: undefined});

      return;
    }

    const orderByDirection = sortByConfig[0].desc ? 'DESC' : 'ASC';
    const orderByField = sortByConfig[0].id.toUpperCase();

    addFilters({orderBy: {direction: orderByDirection, field: orderByField}});
  }

  function handleDelete(id: string) {
    commit({
      variables: {input: {valveFamilyId: id}},
      onCompleted: payload => {
        const entry = payload.deleteValveFamily?.valveFamily;

        if (entry) {
          showEventNotification('removed', 'Type', entry.name);
        }
      },
      updater: store => {
        const root = store.getRoot();

        const connections = ConnectionHandler.getConnections(
          root,
          'ValveFamiliesList_valveFamilies',
        );

        if (!connections) {
          return;
        }

        connections.forEach(connection => {
          ConnectionHandler.deleteNode(connection, id);
        });
      },
    });
  }
}

export default ValveFamiliesTable;

const deleteValveFamilyMutation = graphql`
  mutation ValveFamiliesTableDeleteValveFamilyMutation($input: DeleteValveFamilyInput!) {
    deleteValveFamily(input: $input) {
      valveFamily {
        id
        name
      }
      errors {
        code
        message
      }
    }
  }
`;

const columns = [
  {
    Header: 'Name',
    accessor: 'node.name',
    id: 'name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Created On',
    accessor: 'node.createdAt',
    id: 'created_at',
    Cell({cell: {value}}: any) {
      return <DateCell value={value} />;
    },
  },
  {
    Header: 'Manufacturer',
    accessor: 'node.manufacturer.name',
    id: 'manufacturer_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
];
